import { Suspense, lazy, useState } from "react";
import { BookOpen, ChevronDown, Coins, Users, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils/cn";

import "../i18n";
import { availableKinds } from "../lib/availability";
import type {
  ActionResourceAvailability,
  DirectoryFlags,
  ResourceKind,
} from "../types";

// Only pulled in once someone opens a directory — the menu itself renders on
// every action row, the three directory views are comparatively heavy.
const ResourcePanels = lazy(() => import("./ResourcePanels"));

const ICON: Record<ResourceKind, typeof Users> = {
  expert: Users,
  tool: Wrench,
  financialAid: Coins,
};

export interface ResourceMenuProps {
  /**
   * The *questionnaire* action id — `ActionPlanAction.original_action` on plan
   * surfaces, `Action.id` / `ResultAnswer.action_id` on the autodiagnostic.
   * Favourites and directories are keyed on it, not on the plan row.
   */
  actionId?: string | null;
  /** Effective wording of the action; titles the drawer. */
  actionName: string;
  directories: DirectoryFlags;
  availability: ActionResourceAvailability;
  /**
   * "full" adds the visible "Ressources" label (surfaces with room, like the
   * questionnaire's action cards); "short" is the icon alone for dense rows.
   */
  variant?: "full" | "short";
  /**
   * true opens each directory filtered to the enterprise's own shortlist, with
   * a "see all" escape when it is empty — what the action plan wants, once a
   * shortlist exists. false opens the full directory, for the autodiagnostic
   * where the point is still discovery.
   */
  favoritesDefault?: boolean;
  className?: string;
}

/**
 * The "Ressources" entry point on a piste d'action: experts, outils and aides
 * financières behind one control, each opening its directory in place.
 *
 * Renders nothing when the action has no offerable directory, so callers need no
 * gate of their own.
 */
export function ResourceMenu({
  actionId,
  actionName,
  directories,
  availability,
  variant = "short",
  favoritesDefault = false,
  className,
}: ResourceMenuProps) {
  const { t } = useTranslation();
  // One discriminant rather than a flag per directory: only one panel can be
  // open at a time.
  const [open, setOpen] = useState<ResourceKind | null>(null);

  const kinds = availableKinds(directories, availability);
  if (!actionId || kinds.length === 0) return null;

  const label = t("resources.menuLabel");

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "full" ? (
            <Button variant="outline" size="sm" className={cn("gap-1.5", className)}>
              <BookOpen className="size-4" aria-hidden />
              {label}
              <ChevronDown className="size-3.5 opacity-60" aria-hidden />
            </Button>
          ) : (
            // Dense on purpose: every "short" call site sits in a row of 28px
            // icon controls (reword, comment, delete), where the 44px icon
            // button read as an oversized outlier. Keeps the outline the Django
            // app gives it — it opens a menu, unlike its bare-icon neighbours.
            <Button
              variant="outline"
              size="sm"
              aria-label={label}
              title={label}
              className={cn("size-7 shrink-0 p-0", className)}
            >
              <BookOpen className="size-4" aria-hidden />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          {kinds.map((kind) => {
            const Icon = ICON[kind];
            return (
              <DropdownMenuItem key={kind} onSelect={() => setOpen(kind)}>
                <Icon className="mr-2 size-4" aria-hidden />
                {t(
                  favoritesDefault
                    ? `resources.favorites.${kind}`
                    : `resources.browse.${kind}`,
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {open && (
        <Suspense fallback={null}>
          <ResourcePanels
            kind={open}
            actionId={actionId}
            actionName={actionName}
            favoritesDefault={favoritesDefault}
            onClose={() => setOpen(null)}
          />
        </Suspense>
      )}
    </>
  );
}
